import type { MediaImage, MediaSourceImage, PropertyRoomTypeWithImages } from '../types';
import { getRoomKey } from '../utils';

export { getRoomKey };

export interface RoomImage {
  publicId: string;
  title?: string;
}

export type RoomImagesByKey = Record<string, Array<RoomImage>>;
export type RoomImageOrderOverrides = Record<string, Array<string>>;

export const EMPTY_IMAGE_PUBLIC_IDS: Array<string> = [];

function buildRoomImages(
  images: PropertyRoomTypeWithImages['images'],
  excludedImagePublicIds: Array<string> = [],
): Array<RoomImage> {
  const seenPublicIds = new Set<string>();
  const excludedPublicIds = new Set(excludedImagePublicIds);

  return (images ?? []).reduce<Array<RoomImage>>((roomImages, image: MediaSourceImage) => {
    const publicId = image.id_cloudinary_external;

    if (!publicId || seenPublicIds.has(publicId) || excludedPublicIds.has(publicId)) {
      return roomImages;
    }

    seenPublicIds.add(publicId);
    roomImages.push({
      publicId,
      title: image.title || undefined,
    });

    return roomImages;
  }, []);
}

export function buildRoomImagesByKey(
  propertyRoomTypes: Array<PropertyRoomTypeWithImages>,
  excludedImagePublicIds: Array<string>,
): RoomImagesByKey {
  return propertyRoomTypes.reduce<RoomImagesByKey>((roomImagesByKey, room, index) => {
    roomImagesByKey[getRoomKey(room, index)] = buildRoomImages(room.images, excludedImagePublicIds);
    return roomImagesByKey;
  }, {});
}

export function pruneUnavailableRoomImages(
  roomImagesByKey: RoomImagesByKey,
  mediaImages: Array<MediaImage>,
  publicIdReplacements: Record<string, string> = {},
): RoomImagesByKey {
  const availablePublicIds = new Set(mediaImages.map((image) => image.publicId));

  return Object.fromEntries(
    Object.entries(roomImagesByKey).map(([roomKey, roomImages]) => [
      roomKey,
      roomImages
        .map((image) => ({
          ...image,
          publicId: publicIdReplacements[image.publicId] ?? image.publicId,
        }))
        .filter((image) => availablePublicIds.has(image.publicId)),
    ]),
  );
}

export function applyRoomImageOrderOverrides(
  roomImagesByKey: RoomImagesByKey,
  roomImageOrderOverrides: RoomImageOrderOverrides,
): RoomImagesByKey {
  return Object.entries(roomImageOrderOverrides).reduce((nextRoomImagesByKey, [roomKey, orderedPublicIds]) => {
    const roomImages = nextRoomImagesByKey[roomKey];

    if (!roomImages?.length || orderedPublicIds.length === 0) {
      return nextRoomImagesByKey;
    }

    const imagesByPublicId = new Map(roomImages.map((image) => [image.publicId, image]));
    const orderedPublicIdSet = new Set(orderedPublicIds);
    const nextRoomImages = orderedPublicIds
      .map((publicId) => imagesByPublicId.get(publicId))
      .filter((image): image is RoomImage => !!image)
      .concat(roomImages.filter((image) => !orderedPublicIdSet.has(image.publicId)));

    return {
      ...nextRoomImagesByKey,
      [roomKey]: nextRoomImages,
    };
  }, roomImagesByKey);
}
